import { ListingQueryModel, ApiTotalsModel } from '@loavies/c2c-models'
import { ErrorHandlerService } from '@loavies/utils'

import { mapRelations } from '../response-mappers/relations-mapper'
import { mapListings, mapListing } from '../response-mappers/listing-mappers'
import { PAGINATION } from './constants/pagination'

export class MarketplaceListingsService {
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#show-listing
   * @param {string} id listingId
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  getListingById(id) {
    return this.marketplace.listings
      .show({ id, include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'], ...this.marketplaceService.getImageVariants() })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * @param {string} id listingId
   * @param {number} [minStock]
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  getActiveListingById(id, { minStock } = {}) {
    return this.query({
      query: new ListingQueryModel({
        ids: [id],
        minStock: minStock ?? 1,
      }),
    })
      .then(response => {
        if (!response.data.length) {
          return Promise.reject({
            data: {
              errors: [
                {
                  messageTranslationKey: 'listing_no_longer_available',
                  status: 404,
                },
              ],
            },
          })
        }

        return mapListing(response.data[0])
      })
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }


  /**
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: Object,
   * }>}
   */
  getListings({ page, perPage } = {}) {
    return this.query({
      query: new ListingQueryModel({ minStock: 1 }),
      page,
      perPage,
    })
      .then(response => this.mapMultipleListingsResponse(response))
  }

  /**
   * @param {string[]} ids
   * @return {Promise<loavies.models.listing.ListingModel[]>}
   */
  async getListingsByIds(ids) {
    const queriesCount = Math.ceil(ids.length / PAGINATION.MAXIMUM_PAGE_SIZE)
    const promises = []

    for (let i = 0; i < queriesCount; i++) {
      promises.push(this.query({
        query: new ListingQueryModel({
          ids: ids.slice(i * PAGINATION.MAXIMUM_PAGE_SIZE,  (i + 1) * PAGINATION.MAXIMUM_PAGE_SIZE),
          minStock: 1,
        }),
      }))
    }

    return Promise.all(promises)
      .then(responses => responses
        .map(response => response.data)
        .flat())
      .then(rawListings => rawListings
        .map(rawListing => mapListing(rawListing)))
  }

  /**
   * @param {loavies.models.listing.ListingModel} listing
   * @return {Promise<{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: loavies.models.api.ApiTotalsModel,
   * }>}
   */
  getRelatedListings(listing) {
    return this.query({
      query: new ListingQueryModel({
        publicData: {
          color: listing.publicData?.color,
          productGroup: listing.publicData?.productGroup,
        },
        minStock: 1,
      }),
    })
      .then(response => this.mapMultipleListingsResponse(response))
  }

  /**
   * @param {string} productGroup
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: loavies.models.api.ApiTotalsModel,
   * }>}
   */
  getListingsByProductGroup({ productGroup, page, perPage }) {
    return this.query({
      query: new ListingQueryModel({
        publicData: {
          productGroup,
        },
        minStock: 1,
      }),
      page,
      perPage,
    })
    .then(response => this.mapMultipleListingsResponse(response))
  }

  /**
   * @param {string} id
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: loavies.models.api.ApiTotalsModel,
   * }>}
   */
  getActiveListingsByUserId({ id, page, perPage }) {
    return this.query({
      query: new ListingQueryModel({
        authorId: id,
        minStock: 1,
      }),
      page,
      perPage,
    })
      .then(response => this.mapMultipleListingsResponse(response))
  }

  /**
   * @param {Array} data
   * @param {Object} meta
   * @return {{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: loavies.models.api.ApiTotalsModel
   * }}
   */
  mapMultipleListingsResponse({ data, meta }) {
    return {
      listings: mapListings(data),
      totals: new ApiTotalsModel(meta),
    }
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-listings
   * @param {loavies.models.listing.ListingQueryModel} [query]
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  data: Array,
   *  meta: Object,
   * }>}
   */
  async query({
    query = new ListingQueryModel({}),
    page = PAGINATION.DEFAULT_PAGE,
    perPage = PAGINATION.DEFAULT_ITEMS_PER_PAGE,
  } = {}) {
    return this.marketplace.listings
      .query({
        ...query,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
        page,
        perPage,
      })
      .then(response => mapRelations(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
